import React, { forwardRef } from "react";
import { useInView } from "react-intersection-observer";
import { StaticImage } from "gatsby-plugin-image";

// components
import ReusableForm from "../../Global/ReusableForm/ReusableForm";

// css
import * as styles from "./PartnerForm.module.css";

const PartnerFormComponent = forwardRef(
  (
    {
      onFormSubmit,
      mixpanelPageName = "For Insurers Page",
      buttonColor = "#AD2576",
      checkboxColor = "#AD2576",
      loading = false,
      deviceType = "Desktop",
      operatingSystem = "MacOs",
    },
    ref
  ) => {
    const { ref: inViewRef, inView } = useInView({
      triggerOnce: true,
      threshold: 0.3,
    });

    // Define form fields
    const formFields = [
      {
        name: "organizationName",
        placeholder: "Organization Name*",
        type: "text",
        colSize: 12,
        validation: {
          required: "Please enter your organization name",
          pattern: {
            value: /[a-zA-Z0-9][a-zA-Z0-9 ]*/, // eslint-disable-line no-useless-escape
            message: "Please enter a valid organization name",
          },
          maxLength: {
            value: 100,
            message: "Organization name is too long",
          },
        },
      },
      {
        name: "fullName",
        placeholder: "Full Name*",
        type: "text",
        colSize: 6,
        validation: {
          required: "Please enter your full name",
          pattern: {
            value: /^[A-Za-z\s]+$/, // eslint-disable-line no-useless-escape
            message: "Please enter a valid name",
          },
          maxLength: {
            value: 50,
            message: "Name is too long",
          },
        },
      },
      {
        name: "mobileNumber",
        placeholder: "Mobile Number*",
        type: "text",
        inputMode: "numeric",
        maxLength: 10,
        colSize: 6,
        validation: {
          required: "Please enter your mobile number",
          pattern: {
            value: /^\+?([1-9]{1})\)?[-. ]?([0-9]{5})[-. ]?([0-9]{4})$/, // eslint-disable-line no-useless-escape
            message: "Please enter a valid mobile number",
          },
          maxLength: {
            value: 10,
            message: "Invalid mobile number",
          },
        },
      },
      {
        name: "emailId",
        placeholder: "Email Id*",
        type: "email",
        colSize: 12,
        validation: {
          required: "Please enter your email id",
          pattern: {
            value:
              /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i, // eslint-disable-line no-useless-escape
            message: "Please enter a valid email id",
          },
        },
      },
    ];

    // Handle form submission
    const handleFormSubmit = async data => {
      // Call parent callback if provided
      if (onFormSubmit) {
        await onFormSubmit(data);
      }
    };

    return (
      <section ref={ref} className={styles.formSectionWrapper}>
        <div
          ref={inViewRef}
          className={`container scroll_animate ${inView && "inview"}`}
        >
          {/* Header Section */}
          <div className={styles.headerSection}>
            <h2 className={styles.mainTitle}>
              Partner With Twin Health India Trusted Insurance Partners for
              Better Health
            </h2>
            <p className={styles.mainDescription}>
              Join us in making reversal and metabolic care the new standard in health insurance, improving lives and driving measurable value for your business.
            </p>
          </div>

          <div className="row align-items-center">
            {/* Form Column */}
            <div className="col-lg-6 col-12 order-2 order-lg-1">
              <div className={styles.formContainer}>
                <div className={styles.formHeader}>
                  <h3 className={styles.formTitle}>
                    Improve Member Health & Reduce Claims with Twin Health India
                  </h3>
                  <p className={styles.formDescription}>
                    Learn how Twin Health partners with employers, doctors and
                    insurance providers to deliver clinically proven,
                    all-fees-at-risk programs that improve member health while
                    reducing costs.
                  </p>
                </div>

                <div className={styles.formContent}>
                  <h4 className={styles.formSubtitle}>
                    Tell us a little more about you.
                  </h4>
                  <ReusableForm
                    fields={formFields}
                    onSubmit={handleFormSubmit}
                    ctaText="Submit"
                    loading={loading}
                    mixpanelPageName={mixpanelPageName}
                    formIsModal={false}
                    showCheckbox={true}
                    checkboxText="I agree to be contacted by Twin Health via Call, SMS, RCS, WhatsApp & Email"
                    buttonColor={buttonColor}
                    checkboxColor={checkboxColor}
                    deviceType={deviceType}
                    operatingSystem={operatingSystem}
                  />
                </div>
              </div>
            </div>

            {/* Image Column */}
            <div className="col-lg-6 col-12 order-1 order-lg-2">
              <div className={styles.imageContainer}>
                <StaticImage
                  src="../../../images/forInsurer/form-banner.png"
                  alt="Partner Form Image"
                  className={styles.formImage}
                  placeholder="blurred"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }
);

export default PartnerFormComponent;
